import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  Typography,
} from '@material-ui/core';
import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import {
  Select,
  Input,
  Combobox,
  Checkbox,
  RadioGroup,
  CheckboxGroup,
} from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import { storeUsuario, updateUsuario } from '~/store/modules/usuario/actions';

import { Container } from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector((state) => state.usuario);

  const initials = create ? data : { ...find, ...data };

  const [typeUser, setTypeUser] = useState('U');
  const [userPosto, setUserPosto] = useState([]);

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        codigousuario: create
          ? Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10))
              .test(
                'characters-invalid',
                t('s.configuracao.geral.naopodecontercaracterespecial'),
                function (value) {
                  return validCharacters(value);
                }
              )
          : Yup.string(),
        idperfil:
          typeUser !== 'F'
            ? Yup.string()
                .required(t('s.geral.validacao.campoobrigatorio'))
                .test(
                  'characters-invalid',
                  t('s.configuracao.geral.naopodecontercaracterespecial'),
                  function (value) {
                    return validCharacters(value);
                  }
                )
            : Yup.string().nullable(),
        nomeusuario: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
        emailusuario: Yup.string()
          .nullable()
          .email(t('s.geral.validacao.emailvalido')),
        senhausuario: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(10, t('s.geral.validacao.tamanhomaximo').replace('%s', 10)),
        statususuario: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        tipousuario: Yup.string().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        param5usuariooperador:
          typeUser === 'F' || typeUser === 'A'
            ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string(),
        param7usuariooperador:
          typeUser === 'F' || typeUser === 'A'
            ? Yup.string().required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string(),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ididioma: 'pt-BR',
        statusdbic4usuariooperador: 1,
        param1usuariooperador: 0,
        param2usuariooperador: 0,
        param3usuariooperador: 0,
        param4usuariooperador: 0,
        param5usuariooperador: 0,
        param6usuariooperador: 0,
        param7usuariooperador: 0,
        param8usuariooperador: 0,
        param9usuariooperador: 0,
        ...initials,
        ...JSON.parse(JSON.stringify(param)),
        idcliente: client,
        postos: userPosto,
        tipousuario: typeUser,
        idperfil: typeUser === 'F' ? null : param.idperfil,
      };

      if (create) {
        await dispatch(storeUsuario(objSend, params));
      } else {
        await dispatch(updateUsuario(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  useEffect(() => {
    const { tipousuario, postos } = find;

    if (tipousuario) {
      setTypeUser(tipousuario);
    }

    if (postos) {
      setUserPosto(postos);
    }
  }, [find]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <ExpansionPanel expanded>
            <ExpansionPanelSummary
              aria-controls="panel1a-content"
              id="panel1a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>
                {t('s.cadastro.usuario.informacoesusuario')}
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Container>
                <Input
                  name="codigousuario"
                  label={t('s.cadastro.usuario.codigousuario')}
                  disabled={!create}
                />

                <Input
                  name="nomeusuario"
                  label={t('s.cadastro.usuario.nomeusuario')}
                />

                <Input
                  name="emailusuario"
                  label={t('s.cadastro.usuario.emailusuario')}
                />

                <Input
                  name="senhausuario"
                  label={t('s.cadastro.usuario.senhausuario')}
                  type="password"
                />

                <Select
                  name="tipousuario"
                  label={t('s.cadastro.usuario.tipousuario')}
                  options={[
                    { value: 'U', label: t('s.cadastro.usuario.usuario') },
                    {
                      value: 'F',
                      label: t('s.cadastro.usuario.frentista'),
                    },
                    {
                      value: 'A',
                      label: t('s.cadastro.usuario.usuariofrentista'),
                    },
                  ]}
                  getValue={(value) => setTypeUser(value)}
                />

                {typeUser !== 'F' && (
                  <Combobox
                    name="idperfil"
                    label="nomeperfil"
                    url="perfis"
                    unique="idperfil"
                    title={t('s.cadastro.usuario.idperfil')}
                  />
                )}

                <Select
                  name="statususuario"
                  label={t('s.cadastro.usuario.statususuario')}
                  options={[
                    { value: 'L', label: t('s.geral.opcoes.liberado') },
                    { value: 'B', label: t('s.geral.opcoes.bloqueado') },
                    { value: 'C', label: t('s.geral.opcoes.cancelado') },
                  ]}
                />
              </Container>
            </ExpansionPanelDetails>
            <ExpansionPanelSummary
              aria-controls="panel2a-content"
              id="panel2a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>{t('s.cadastro.usuario.operacao')}</Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Container>
                <Typography style={{ marginBottom: '1rem' }}>
                  {t('s.cadastro.usuario.operacoesterminal')}
                </Typography>

                <Checkbox
                  name="param1usuariooperador"
                  label={t('s.cadastro.usuario.liberaafericao')}
                  disabled={typeUser === 'U'}
                  serialize={(value) => Number(value)}
                />

                <Checkbox
                  name="param2usuariooperador"
                  label={t('s.cadastro.usuario.recebecombustivel')}
                  disabled={typeUser === 'U'}
                  serialize={(value) => Number(value)}
                />

                <Checkbox
                  name="param3usuariooperador"
                  label={t('s.cadastro.usuario.acertaestoque')}
                  disabled={typeUser === 'U'}
                  serialize={(value) => Number(value)}
                />

                <Checkbox
                  name="param4usuariooperador"
                  label={t('s.cadastro.usuario.acertaencerrante')}
                  disabled={typeUser === 'U'}
                  serialize={(value) => Number(value)}
                />

                <Checkbox
                  name="param6usuariooperador"
                  label={t('s.cadastro.usuario.associatransponder')}
                  disabled={typeUser === 'U'}
                  style={{ marginBottom: '3rem' }}
                  serialize={(value) => Number(value)}
                />

                <RadioGroup
                  name="param5usuariooperador"
                  label={t('s.cadastro.usuario.trocamodooperacional')}
                  options={[
                    {
                      label: t(
                        's.cadastro.usuario.trocamodooperacional.naolibera'
                      ),
                      value: '0',
                    },
                    {
                      label: t(
                        's.cadastro.usuario.trocamodooperacional.liberamanualintermitente'
                      ),
                      value: '1',
                    },
                    {
                      label: t(
                        's.cadastro.usuario.trocamodooperacional.liberamanualconstante'
                      ),
                      value: '2',
                    },
                  ]}
                  disabled={typeUser === 'U'}
                  serialize={(value) => Number(value)}
                />

                <RadioGroup
                  name="param7usuariooperador"
                  label={t('s.cadastro.usuario.liberacaoAbastecimento')}
                  options={[
                    {
                      label: t(
                        's.cadastro.usuario.liberacaoabastecimento.naolibera'
                      ),
                      value: '0',
                    },
                    {
                      label: t(
                        's.cadastro.usuario.liberacaoabastecimento.comtranspondercadastrado'
                      ),
                      value: '1',
                    },
                    {
                      label: t(
                        's.cadastro.usuario.liberacaoabastecimento.comtranspondernaocadastrado'
                      ),
                      value: '2',
                    },
                    {
                      label: t(
                        's.cadastro.usuario.liberacaoabastecimento.semtransponder'
                      ),
                      value: '3',
                    },
                  ]}
                  disabled={typeUser === 'U'}
                  serialize={(value) => Number(value)}
                />
              </Container>
            </ExpansionPanelDetails>
            <ExpansionPanelSummary
              aria-controls="panel3a-content"
              id="panel3a-header"
              style={{
                background: 'rgba(0, 0, 0, 0.05)',
                borderBottom: '1px solid rgba(0,0,0,.2)',
              }}
            >
              <Typography>
                {t('s.cadastro.usuario.titulopainel.postosusuario')}
              </Typography>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Container>
                <CheckboxGroup
                  name="postos"
                  title={t('s.cadastro.usuario.acessotodospostos')}
                  label="nomeposto"
                  unique="idposto"
                  url="postoscomboios"
                  getValue={(value) => setUserPosto(value)}
                />
              </Container>
            </ExpansionPanelDetails>
          </ExpansionPanel>
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
